<ng-container *ngFor="let data of bolData; let last = last">
	<table class="print-layout-table">
		<thead>
			<tr>
				<th>
					<div class="print-header-content">
						<span style="font-weight: 700; font-size: 1rem;">{{ data.job?.code }}</span>
						<span style="margin: 0 0.5rem; color: #9ca3af;">|</span>
						<span>{{ formattedDate }}</span>
						<span style="margin: 0 0.5rem; color: #9ca3af;">|</span>
						<span style="font-weight: 600;">{{ formatCustomers(data.job) }}</span>
						<span style="margin: 0 0.5rem; color: #9ca3af;">|</span>
						<span style="font-weight: 500;">
							<ng-container *ngFor="let truck of getTrucks(data.event); let last = last">
								{{ truck.name }}<span *ngIf="!last">, </span>
							</ng-container>
							<span *ngIf="getTrucks(data.event).length === 0">No Asset</span>
						</span>
					</div>
				</th>
			</tr>
		</thead>
		<tbody>
			<tr>
				<td>
					<div
						class="sheet page-break"
						[class.page-break]="!last || (data.job?.artifacts?.length ?? 0) > 0"
					>
						<!-- Document Header -->
						<div class="bol-header">
							<div class="brand-section">
								<img
									*ngIf="logoUrl"
									[src]="logoUrl"
									class="brand-logo"
									alt="Company Logo"
								>
								<div
									class="company-name"
									[style.color]="primaryColor"
								>{{ companyName }}</div>
								<div class="document-type">
									{{ formattedDate }} - {{ data.event.type | titlecase }}
									<ng-container
										*ngIf="data.event.type.toLowerCase() !== data.event.title.toLowerCase()"
									>
										- {{ data.event.title }}
									</ng-container>
								</div>
							</div>
							<div class="meta-section">
								<div class="meta-group">
									<div class="label">Job Number</div>
									<div class="value">{{ data.job?.code }}</div>
								</div>
								<div class="meta-group">
									<div class="label">Date Issued</div>
									<div class="value">{{ formattedDate }}</div>
								</div>
							</div>
						</div>

						<!-- Job Overview (Crew & Times) -->
						<div class="job-overview">
							<ng-container *ngFor="let customer of getCustomers(data.job)">
								<div class="overview-item">
									<div class="label">Customer</div>
									<div class="value">{{ customer.givenName }} {{ customer.familyName }}</div>
								</div>
								<div class="overview-item">
									<div class="label">Phone</div>
									<div class="value">{{ customer.phone || '-' }}</div>
								</div>
								<div class="overview-item">
									<div class="label">Email</div>
									<div
										class="value"
										style="word-break: break-all;"
									>{{ customer.email || '-' }}</div>
								</div>
							</ng-container>

							<div class="overview-item">
								<div class="label">Service Date</div>
								<div class="value">
									<svg
										class="inline-icon"
										viewBox="0 0 24 24"
										aria-hidden="true"
										focusable="false"
									>
										<rect
											x="3"
											y="5"
											width="18"
											height="16"
											rx="2"
										></rect>
										<line
											x1="16"
											y1="3"
											x2="16"
											y2="7"
										></line>
										<line
											x1="8"
											y1="3"
											x2="8"
											y2="7"
										></line>
										<line
											x1="3"
											y1="11"
											x2="21"
											y2="11"
										></line>
									</svg>
									{{ formatDate(data.event.start, 'MMM D') }}{{ getOrdinal(data.event.start) }}
								</div>
							</div>
							<div class="overview-item">
								<div class="label">Arrival Window</div>
								<div class="value">
									<svg
										class="inline-icon"
										viewBox="0 0 24 24"
										aria-hidden="true"
										focusable="false"
									>
										<circle
											cx="12"
											cy="12"
											r="9"
										></circle>
										<polyline points="12 7 12 12 15.5 14"></polyline>
									</svg>
									{{ getArrivalWindow(data.event) }}
								</div>
							</div>
							<div class="overview-item">
								<div class="label">Est. Completion</div>
								<div class="value">{{ formatDate(data.event.end, 'h:mm A') }} ({{
									getEstimateHours(data.event) }} hrs)
								</div>
							</div>

							<div class="overview-item">
								<div class="label">Event Total</div>
								<div class="value">{{ formatMoney(data.job?.total || 0, data.job?.currency) }}</div>
							</div>
							<div class="overview-item">
								<div class="label">Paid</div>
								<div class="value">{{ formatMoney(data.job?.paidTotal || 0, data.job?.currency) }}</div>
							</div>
							<div class="overview-item">
								<div class="label">Balance</div>
								<div class="value">{{ formatMoney((data.job?.total || 0) - (data.job?.paidTotal || 0),
									data.job?.currency) }}</div>
							</div>

							<div class="overview-item full-width">
								<div class="label">Assigned Crew</div>
								<div class="value">
									<ng-container *ngIf="getCrew(data.event).length > 0; else noCrew">
										<div *ngFor="let member of getCrew(data.event)">
											{{ member.user.givenName }} {{ member.user.familyName }} ({{ member.role }})
										</div>
									</ng-container>
									<ng-template #noCrew>No Crew Assigned</ng-template>
								</div>
							</div>
						</div>

						<!-- Route Information -->
						<div class="route-section-wrapper">
							<!-- Locations -->
							<div class="locations-list">
								<div
									class="route-point"
									*ngFor="let loc of getLocations(data); let i = index; let isLast = last"
								>
									<!-- locationType here are values from: locationNameMap defined in global.constants.ts-->
									<div
										class="dot-marker"
										[class.bg-green]="loc.locationType === 'start'"
										[class.bg-red]="loc.locationType === 'end'"
										[class.bg-blue]="loc.locationType !== 'start' && loc.locationType !== 'end'"
									></div>
									<div class="route-details">
										<h3 class="route-label">
											{{ loc.locationType | titlecase }}
										</h3>
										<div class="address">
											<div class="full-address">{{ loc.location?.addressLineOne || '' }}</div>
											{{ loc.location?.city }}, {{ loc.location?.['countryJurisdiction'] ||
											loc.location?.state }}
											{{
											loc.location?.areaCode || loc.location?.postalCode }}
										</div>
									</div>
								</div>
								<div
									*ngIf="getLocations(data).length === 0"
									class="text-gray p-4"
								>No locations specified</div>
							</div>
						</div>

						<!-- Inventory Section -->
						<div class="inventory-section">
							<div class="section-header">
								<h3>Job Inventory</h3>
								<div class="totals">
									<span class="total-badge">Vol: {{ (getInventory(data.job)?.volume || 0) | number }}
										ft³</span>
									<span class="total-badge">Wgt: {{ (getInventory(data.job)?.weight || 0) | number }}
										lbs</span>
								</div>
							</div>

							<table class="data-table">
								<thead>
									<tr>
										<th class="col-name">Item Name</th>
										<th class="col-qty text-center">Qty</th>
										<th class="col-wgt text-right">Est. Weight</th>
									</tr>
								</thead>
								<tbody *ngFor="let room of getInventory(data.job)?.rooms">
									<tr class="room-header">
										<td
											colspan="3"
											class="font-bold"
										>{{ room.name }}</td>
									</tr>
									<tr *ngFor="let item of room.inventory">
										<td class="col-name">{{ item.name }}</td>
										<td class="col-qty text-center font-bold">{{ item.quantity }}</td>
										<td class="col-wgt text-right">{{ item.weight }} lbs</td>
									</tr>
								</tbody>
								<tbody *ngIf="!getInventory(data.job)?.rooms?.length">
									<tr>
										<td
											colspan="3"
											class="text-center text-gray py-4"
										>No detailed inventory available</td>
									</tr>
								</tbody>
							</table>
						</div>

						<!-- Charges & Totals -->
						<div class="charges-section">
							<h3 class="section-title">Charges</h3>
							<table class="data-table">
								<thead>
									<tr>
										<th class="col-desc">Description</th>
										<th class="col-rate text-right">Rate</th>
										<th class="col-hours text-center">Qty/Hrs</th>
										<th class="col-amount text-right">Amount</th>
									</tr>
								</thead>
								<tbody>
									<tr *ngFor="let charge of getCharges(data.job)">
										<td class="col-desc">
											<div class="font-bold">{{ charge.productName || charge.product?.name ||
												'Item' }}</div>
										</td>
										<td class="col-rate text-right">
											<ng-container *ngIf="charge.price">
												{{ formatMoney(charge.price.amount, data.job?.currency) }}
												<span *ngIf="charge.price.priceType === 'hourly'">/hr</span>
											</ng-container>
											<ng-container *ngIf="!charge.price">-</ng-container>
										</td>
										<td class="col-hours text-center">{{ charge.amount || 1 }}</td>
										<td class="col-amount text-right font-bold">{{ formatMoney(charge.total,
											data.job?.currency) }}
										</td>
									</tr>
									<tr *ngIf="getCharges(data.job).length === 0">
										<td class="col-desc">
											<div class="font-bold">Labor Service & Fees</div>
											<div class="sub-text">See Invoice for details</div>
										</td>
										<td class="col-rate text-right">-</td>
										<td class="col-hours text-center">-</td>
										<td class="col-amount text-right font-bold">{{
											formatMoney(data.job?.discountedSubTotal || 0,
											data.job?.currency) }}
										</td>
									</tr>
								</tbody>
								<tfoot>
									<tr>
										<td
											colspan="3"
											class="total-label text-right"
										>Subtotal:</td>
										<td class="text-right font-bold">{{ formatMoney(data.job?.subTotal || 0,
											data.job?.currency) }}
										</td>
									</tr>
									<tr *ngIf="(data.job?.discountTotal || 0) > 0">
										<td
											colspan="3"
											class="total-label text-right"
										>Discount:</td>
										<td class="text-right font-bold">-{{ formatMoney(data.job?.discountTotal || 0,
											data.job?.currency) }}</td>
									</tr>
									<tr *ngIf="(data.job?.taxTotal || 0) > 0">
										<td
											colspan="3"
											class="total-label text-right"
										>Tax:</td>
										<td class="text-right font-bold">{{ formatMoney(data.job?.taxTotal || 0,
											data.job?.currency) }}
										</td>
									</tr>
									<tr>
										<td
											colspan="3"
											class="total-label text-right"
										>Total:</td>
										<td
											class="total-amount text-right"
											[style.color]="primaryColor"
										>{{ formatMoney(data.job?.total || 0, data.job?.currency) }}</td>
									</tr>
								</tfoot>
							</table>
						</div>

						<div
							class="closing-section"
						>
							<!-- Notes & Instructions -->
							<div
								class="notes-section"
								*ngIf="getNotes(data.job) as notes"
							>
								<h4 class="notes-title">Notes / Special Instructions</h4>
								<p class="notes-content">"{{ notes }}"</p>
							</div>

							<div
								class="notes-section"
								*ngIf="getCustomerNotesContent(data.job) as customerNotes"
							>
								<h4 class="notes-title">Customer Notes</h4>
								<quill-view
									class="notes-content"
									[content]="customerNotes.content"
									[format]="customerNotes.format"
									theme="snow"
									[modules]="{toolbar: false}"
									[readOnly]="true"
								></quill-view>
							</div>

							<div
								class="notes-section"
								*ngIf="getCrewNotesContent(data) as crewNotes"
							>
								<h4 class="notes-title">Crew Notes</h4>
								<quill-view
									class="notes-content"
									[content]="crewNotes.content"
									[format]="crewNotes.format"
									theme="snow"
									[modules]="{toolbar: false}"
									[readOnly]="true"
								></quill-view>
							</div>

							<!-- Linked Documents (Static Representation) -->
							<div
								class="documents-list-section"
								*ngIf="hasAttachedDocuments(data.job)"
							>
								<h4 class="docs-title">
									<svg
										class="inline-icon"
										viewBox="0 0 24 24"
										aria-hidden="true"
										focusable="false"
									>
										<path
											d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 1 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.82-2.82l8.49-8.48"
										></path>
									</svg>
									Attached Job Documents
								</h4>
								<div class="docs-links">
									<ng-container *ngFor="let artifact of data.job?.artifacts">
										<div
											class="doc-link"
											*ngIf="isAttachableArtifact(artifact)"
										>
											<svg
												class="inline-icon"
												viewBox="0 0 24 24"
												aria-hidden="true"
												focusable="false"
											>
												<path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z">
												</path>
												<polyline points="14 2 14 8 20 8"></polyline>
											</svg>
											{{ artifact.name }}
										</div>
									</ng-container>
								</div>
							</div>

							<!-- Signatures -->
							<div class="signatures-section">
								<div
									class="terms-text"
									*ngIf="getContractContent(data) as contract"
								>
									<quill-view
										[content]="contract.content"
										[format]="contract.format"
										theme="snow"
										[modules]="{toolbar: false}"
										[readOnly]="true"
									></quill-view>
								</div>

								<div class="signatures-grid">
									<div class="signature-box">
										<div class="sig-line"></div>
										<label>Shipper / Customer Signature</label>
									</div>
									<div class="signature-box">
										<div class="sig-line"></div>
										<label>Carrier / Driver Signature</label>
									</div>
								</div>
							</div>
						</div>

					</div>

					<!-- Attached Documents Pages -->
					<ng-container *ngFor="let artifact of data.job?.artifacts; let isLastDoc = last">
						<!-- Image Handling -->
						<div
							class="sheet attachment-sheet page-break"
							*ngIf="isAttachableImageArtifact(artifact)"
							[class.page-break]="!last || !isLastDoc"
						>
							<div class="attachment-header">
								<h2>Attachment: {{ artifact.name }}</h2>
							</div>
							<div class="attachment-content">
								<img
									[src]="getArtifactUrl(artifact) || ''"
									[alt]="artifact.name"
								>
							</div>
						</div>

						<!-- PDF Handling -->
						<ng-container
							*ngIf="isAttachablePdfArtifact(artifact)"
						>
							<ng-container *ngIf="pdfPages.get(getArtifactUrl(artifact) || '') as pages">
								<div
									class="sheet attachment-sheet page-break"
									*ngFor="let pageDataUrl of pages; let pageIndex = index; let isLastPage = last"
									[class.page-break]="!last || !isLastDoc || !isLastPage"
								>
									<div class="attachment-header">
										<h2>Attachment: {{ artifact.name }} (Page {{ pageIndex + 1 }})</h2>
									</div>
									<div class="attachment-content">
										<img
											[src]="pageDataUrl"
										>
									</div>
								</div>
							</ng-container>
						</ng-container>
					</ng-container>
				</td>
			</tr>
		</tbody>
	</table>
</ng-container>
